import { useState, useEffect, memo } from "react";
import { TrendingUp, ExternalLink, ChevronRight } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";

const TrendingStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrends = async () => {
      try {
        const res = await axios.get(`${API}/ai/trends?size=5`);
        setStories(res.data.stories || []);
      } catch (e) {
        console.error("Trends error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchTrends();
  }, []);

  if (loading || stories.length === 0) return null;

  return (
    <div className="mb-6 lg:mb-10" data-testid="trending-stories">
      <div className="flex items-center gap-2 mb-3 lg:mb-5">
        <div className="p-1.5 bg-blue-500/10 rounded-lg">
          <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-blue-400" />
        </div>
        <h2 className="text-base lg:text-xl font-bold text-white">Dünyada Gündem</h2>
        <span className="text-[10px] lg:text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">AI</span>
      </div>
      
      {/* Mobile: Horizontal Scroll */}
      <div className="lg:hidden -mx-4 px-4">
        <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 snap-x">
          {stories.slice(0, 5).map((story, i) => {
            const sentimentObj = story.sentiment || {};
            const pos = sentimentObj.positive || 0;
            const neg = sentimentObj.negative || 0;
            const sentimentLabel = pos > neg ? "Pozitif" : neg > pos ? "Negatif" : "Nötr";
            const sentimentColor = pos > neg ? "text-emerald-400" : neg > pos ? "text-red-400" : "text-zinc-400";

            return (
              <div
                key={story.id || i}
                className="flex-shrink-0 w-[200px] bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-3 snap-start"
                data-testid={`trend-mobile-${i}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500/20 text-blue-400 rounded-lg flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <h3 className="text-xs font-semibold text-white line-clamp-2 leading-snug">
                    {story.name}
                  </h3>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-zinc-500">{story.article_count} haber</span>
                  <span className={sentimentColor}>{sentimentLabel}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop: Grid */}
      <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {stories.slice(0, 5).map((story, i) => {
          const sentimentObj = story.sentiment || {};
          const pos = sentimentObj.positive || 0;
          const neg = sentimentObj.negative || 0;
          const sentimentLabel = pos > neg ? "Pozitif" : neg > pos ? "Negatif" : "Nötr";
          const sentimentColor = pos > neg ? "text-emerald-400" : neg > pos ? "text-red-400" : "text-zinc-400";

          return (
            <div
              key={story.id || i}
              className="group bg-zinc-900/50 border border-zinc-800/50 hover:border-blue-500/30 rounded-xl p-4 transition-all"
              data-testid={`trend-${i}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <span className="flex-shrink-0 w-7 h-7 bg-blue-500/20 text-blue-400 rounded-lg flex items-center justify-center text-sm font-bold">{i + 1}</span>
                <h3 className="text-sm font-semibold text-white group-hover:text-blue-300 transition-colors line-clamp-2 leading-snug">
                  {story.name}
                </h3>
              </div>
              {story.summary && (
                <p className="text-xs text-zinc-500 line-clamp-2 mb-3">{story.summary.slice(0, 100)}</p>
              )}
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-500">{story.article_count} haber</span>
                <span className={sentimentColor}>{sentimentLabel}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default memo(TrendingStories);
